import _toConsumableArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TableRow from '@tiptap/extension-table-row';
import { BubbleMenu } from '@tiptap/extension-bubble-menu';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import styled from 'styled-components';
import { Color } from '@tiptap/extension-color';
import { MenuEditor } from './component/menu-editor.component';
import TableBubbleMenu from './component/bubble-table.component';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ImageResize } from './extensions/image-resize';
import { CustomTable, CustomTableCell, CustomTableHeader } from './extensions/table';
import { TextAlign } from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { CommentExtension } from './extensions/comment';
import { Button } from 'antd';
import { styleVariables } from '../../style';
import { MathExtension } from './extensions/math'; // Re-export types from menu-editor for convenience

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var StyledLayout = styled.div.withConfig({
  displayName: "editorcomponent__StyledLayout",
  componentId: "sc-f4dfm9-0"
})([".tiptap{table{border-collapse:collapse;margin:0;overflow:hidden;table-layout:fixed;width:100%;td,th{position:relative;p{position:inherit;z-index:2;}}.selectedCell::after{background-color:#cbe1f79e;content:'';left:0;right:0;top:0;bottom:0;pointer-events:none;position:absolute;z-index:1;}.column-resize-handle{background-color:", ";bottom:-2px;pointer-events:none;position:absolute;right:-2px;top:0;width:4px;}}.tableWrapper{margin:1.5rem 0;overflow-x:auto;}&.resize-cursor{cursor:ew-resize;cursor:col-resize;}}.ProseMirror{padding:", ";border:", " solid ", ";border-bottom-left-radius:", ";border-bottom-right-radius:", ";background-color:", ";outline:none !important;img{max-width:100%;}}p.is-editor-empty:first-child::before{color:", ";font-style:italic;content:attr(data-placeholder);float:left;height:0;pointer-events:none;}"], styleVariables.colors.gray6, styleVariables.padding.large, styleVariables.borderWidth.small, styleVariables.colors.gray6, styleVariables.borderRadius.medium, styleVariables.borderRadius.medium, styleVariables.colors.gray1, styleVariables.colors.gray9);
var SaveButtonContainer = styled.div.withConfig({
  displayName: "editorcomponent__SaveButtonContainer",
  componentId: "sc-f4dfm9-1"
})(["display:flex;justify-content:flex-end;margin-top:", ";button{background-color:", ";border-color:", ";&:hover{background-color:", ";border-color:", ";}}"], styleVariables.margin.medium, styleVariables.colors.blue3, styleVariables.colors.blue3, styleVariables.colors.blue4, styleVariables.colors.blue4);
var StyledEditorContent = styled(EditorContent).withConfig({
  displayName: "editorcomponent__StyledEditorContent",
  componentId: "sc-f4dfm9-2"
})(["max-height:", ";overflow-y:", ";& > *{pointer-events:", ";}"], function (props) {
  return props.scrollable ? '70vh' : 'none';
}, function (props) {
  return props.scrollable ? 'auto' : 'visible';
}, function (props) {
  return props.disabled && 'none';
});
export var RichTextEditor = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var addImage = _ref.addImage,
      placeholder = _ref.placeholder,
      onChange = _ref.onChange,
      onUpdate = _ref.onUpdate,
      content = _ref.content,
      showComment = _ref.showComment,
      addAudio = _ref.addAudio,
      editorLabels = _ref.editorLabels,
      id = _ref.id,
      disabled = _ref.disabled,
      _ref$scrollable = _ref.scrollable,
      scrollable = _ref$scrollable === void 0 ? false : _ref$scrollable,
      _ref$autoSave = _ref.autoSave,
      autoSave = _ref$autoSave === void 0 ? true : _ref$autoSave,
      _ref$saveButtonText = _ref.saveButtonText,
      saveButtonText = _ref$saveButtonText === void 0 ? 'Lưu' : _ref$saveButtonText,
      customMenuItems = _ref.customMenuItems,
      menuGroupsOrder = _ref.menuGroupsOrder,
      _ref$customExtensions = _ref.customExtensions,
      customExtensions = _ref$customExtensions === void 0 ? [] : _ref$customExtensions,
      onEditorReady = _ref.onEditorReady,
      _ref$hideMath = _ref.hideMath,
      hideMath = _ref$hideMath === void 0 ? false : _ref$hideMath;

  var _useState = useState([]),
      comments = _useState[0],
      setComments = _useState[1];

  var _useState2 = useState(null),
      activeCommentId = _useState2[0],
      setActiveCommentId = _useState2[1];

  var commentsSectionRef = useRef(null);

  var _useState3 = useState(''),
      editorContent = _useState3[0],
      setEditorContent = _useState3[1];

  var focusCommentWithActiveId = function focusCommentWithActiveId(id) {
    if (!commentsSectionRef.current) return;
    var commentInput = commentsSectionRef.current.querySelector("input#".concat(id));
    if (!commentInput) return;
    commentInput.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center'
    });
  };

  var onUpdateRef = useRef(onUpdate);
  var onChangeRef = useRef(onChange);
  useEffect(function () {
    onUpdateRef.current = onUpdate;
    onChangeRef.current = onChange;
  }, [onUpdate, onChange]);
  var editor = useEditor({
    extensions: [StarterKit, ImageResize, TextAlign.configure({
      types: ['heading', 'paragraph']
    }), Image.configure({
      inline: true
    }), Underline, TableRow, CustomTable, CustomTableCell, CustomTableHeader, TextStyle, Color, BubbleMenu, MathExtension, Placeholder.configure({
      placeholder: placeholder || 'Write something...'
    }), CommentExtension.configure({
      HTMLAttributes: {
        "class": 'my-comment'
      },
      onCommentActivated: function onCommentActivated(commentId) {
        setActiveCommentId(commentId);
        if (commentId) setTimeout(function () {
          return focusCommentWithActiveId(commentId);
        });
      }
    })].concat(_toConsumableArray(customExtensions)),
    content: content === placeholder ? '' : content,
    onUpdate: function onUpdate(_ref2) {
      var editor = _ref2.editor;
      var html = editor.getHTML();
      setEditorContent(html);

      if (onUpdateRef.current) {
        onUpdateRef.current(html);
      }

      if (autoSave && onChangeRef.current) {
        onChangeRef.current(html);
      }
    }
  }); // Hàm xử lý nút lưu

  var handleSave = useCallback(function () {
    if (editor && onChange) {
      onChange(editor.getHTML());
    }
  }, [editor, onChange]);
  var insertImageHandler = useCallback(function (file) {
    if (editor) {
      editor.chain().focus().setImage({
        src: file
      }).run();
      editor.chain().focus().setTextSelection(editor.state.selection.$anchor.pos + 1).run();
    }
  }, [editor]);
  useImperativeHandle(ref, function () {
    return {
      insertImageHandler: insertImageHandler,
      editor: editor
    };
  }); // Call onEditorReady when editor is first created

  useEffect(function () {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);
  useEffect(function () {
    if (editor && content !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);
  useEffect(function () {
    if (!activeCommentId) return;
    focusCommentWithActiveId(activeCommentId);
  }, [activeCommentId]);
  useEffect(function () {
    return function () {
      if (editor) editor.destroy();
    };
  }, [editor]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(StyledLayout, {
      children: [/*#__PURE__*/_jsx(MenuEditor, {
        editor: editor,
        addImage: addImage,
        addAudio: addAudio,
        labels: editorLabels,
        customMenuItems: customMenuItems,
        menuGroupsOrder: menuGroupsOrder,
        hideMath: hideMath
      }), /*#__PURE__*/_jsx(StyledEditorContent, {
        editor: editor,
        disabled: disabled,
        scrollable: scrollable
      }), /*#__PURE__*/_jsx(TableBubbleMenu, {
        editor: editor,
        labels: editorLabels
      }), !autoSave && onChange && /*#__PURE__*/_jsx(SaveButtonContainer, {
        children: /*#__PURE__*/_jsx(Button, {
          type: "primary",
          onClick: handleSave,
          children: saveButtonText
        })
      })]
    })
  });
});